// useAuthoringDraftState — the shell's authoring/draft/export surface state
// cells: the active tab's resolved explicit + ambient context items, the
// export flow's machine + finished-package handle, the active material's
// template identity, and the create dialog's catalogue + submission progress.
// All park-only at the foundation (declared and returned, never read in the
// body), so lifting them here keeps useShellFoundation under the size gate
// without changing behaviour. Called ONCE, unconditionally, at the same
// position its cells occupied in the foundation body, so the hook-call order
// is unchanged.
export { useAuthoringDraftState };

import { useState } from "react";
import type { AmbientContextItem } from "../../components/Chat/types.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type { ExportState } from "../../components/ExportFlow/ExportFlow.js";
import type {
    MaterialCreateState,
    TemplateCatalog,
} from "../../components/MaterialCreate/MaterialCreate.js";
import type { ExportReady } from "../ports.js";

const useAuthoringDraftState = () => {
    // The ACTIVE TAB's EXPLICIT context (context spine step 3a), resolved
    // to displayable refs for the always-visible strip. Re-read whenever
    // the active anchor changes (tab switch) and after every
    // introduce/drop/clear this host dispatches — see the effect + the
    // three handlers below.
    const [activeContextItems, setActiveContextItems] = useState<
        readonly ComposerResourceRef[]
    >([]);
    // The active tab's AMBIENT bloom (context spine step 3b), resolved the
    // SAME way as `activeContextItems` above (bare resource ids -> `refFor`
    // -> `toComposerResourceRef`). Reloaded on the same triggers: anchor
    // change (tab switch) AND every explicit introduce/drop/clear this host
    // dispatches — promoting an ambient item must make it disappear from
    // here and appear in `activeContextItems` in the same round-trip.
    const [activeAmbientItems, setActiveAmbientItems] = useState<
        readonly AmbientContextItem[]
    >([]);
    const [exportState, setExportState] = useState<ExportState>({
        type: "choosing",
    });
    // The finished package's fetch URL, held between packaging (done) and the
    // author pressing Download. Null until a package lands.
    const [exportReady, setExportReady] = useState<ExportReady | null>(null);
    // The active material's template identity ({ id, version } wire primitives),
    // captured on load — gates the export-format dropdown to the codec-backed
    // targets for THIS template (REQ-FORMATS-001). Null when no material is
    // focused or the wire omitted it (then the full list is offered).
    const [activeMaterialTemplate, setActiveMaterialTemplate] = useState<{
        readonly id: string;
        readonly version: number;
    } | null>(null);
    // The creation dialog's inputs: the template catalogue (loaded once on
    // mount — the built-in shapes are static per session) and the
    // submission's progress (reset whenever the dialog opens).
    const [templateCatalog, setTemplateCatalog] = useState<TemplateCatalog>({
        type: "loading",
    });
    const [createState, setCreateState] = useState<MaterialCreateState>({
        type: "idle",
    });

    return {
        activeContextItems,
        setActiveContextItems,
        activeAmbientItems,
        setActiveAmbientItems,
        exportState,
        setExportState,
        exportReady,
        setExportReady,
        activeMaterialTemplate,
        setActiveMaterialTemplate,
        templateCatalog,
        setTemplateCatalog,
        createState,
        setCreateState,
    };
};
